import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import _ from 'lodash';

import { Icon } from '../../shared/Icon';

import {
    getApiErrorMessage,
    GiftSchemas,
    useGiftAdmin,
    useGiftApi,
} from '../../hooks/useGiftApi';

import { roundToClosestMidnightUtc } from '../utils/dates';

import { RouteTo } from '../../routes';

import { computePublicationAction } from '../domain/importPublications';

export interface ImportGiftEntry {
    legacyGiftId: string;
    id: string;
    contents: string[];
    wrapperGameElementId: string;
}

interface ImportPreview {
    newGifts: Array<{
        entry: ImportGiftEntry;
        startDate: string;
        endDate: string;
    }>;
    updateGifts: Array<{
        entry: ImportGiftEntry;
        startDate: string;
        endDate: string;
    }>;
    publicationActions: Array<{
        entry: ImportGiftEntry;
        startDate: string;
        endDate: string;
        action: ReturnType<typeof computePublicationAction>;
    }>;
    errors: string[];
}

export function parseImportJson(text: string): ImportGiftEntry[] | string {
    try {
        const parsed = JSON.parse(text);
        if (!Array.isArray(parsed)) {
            return 'JSON must be an array';
        }
        for (let i = 0; i < parsed.length; i++) {
            const item = parsed[i];
            if (!item.legacyGiftId || typeof item.legacyGiftId !== 'string') {
                return `Item ${i}: missing or invalid "legacyGiftId"`;
            }
            if (!item.id || typeof item.id !== 'string') {
                return `Item ${i}: missing or invalid "id"`;
            }
            if (!Array.isArray(item.contents)) {
                return `Item ${i}: missing or invalid "contents" (must be array)`;
            }
            if (
                !item.wrapperGameElementId ||
                typeof item.wrapperGameElementId !== 'string'
            ) {
                return `Item ${i}: missing or invalid "wrapperGameElementId"`;
            }
        }
        return parsed as ImportGiftEntry[];
    } catch {
        return 'Invalid JSON';
    }
}

function computePreview(
    entries: ImportGiftEntry[],
    legacyGifts: Array<GiftSchemas['GiftDTO']>,
    existingV2Gifts: GiftSchemas['GetGiftAdminDTO'],
): ImportPreview {
    const legacyGiftMap = _.keyBy(legacyGifts, 'giftId');
    const existingGiftsMap = new Map(
        existingV2Gifts.gifts.map((g) => [g.id, g]),
    );
    const newGifts: ImportPreview['newGifts'] = [];
    const updateGifts: ImportPreview['updateGifts'] = [];
    const publicationActions: ImportPreview['publicationActions'] = [];
    const errors: string[] = [];

    for (const entry of entries) {
        const legacyGift = legacyGiftMap[entry.legacyGiftId];
        if (!legacyGift) {
            errors.push(`Legacy gift "${entry.legacyGiftId}" not found`);
            continue;
        }

        const mapped = {
            entry,
            startDate: roundToClosestMidnightUtc(legacyGift.startDate),
            endDate: roundToClosestMidnightUtc(legacyGift.endDate),
        };

        const existingGift = existingGiftsMap.get(entry.id);
        if (!existingGift) {
            newGifts.push(mapped);
        } else if (hasChanged(existingGift, mapped)) {
            updateGifts.push(mapped);
        }

        const action = computePublicationAction(
            existingGift?.publications ?? [],
            mapped.startDate,
            mapped.endDate,
        );
        if (action.type !== 'skip') {
            publicationActions.push({ ...mapped, action });
        }
    }

    return { newGifts, updateGifts, publicationActions, errors };
}

function hasChanged(
    existing: GiftSchemas['FullGiftAdminItem'],
    mapped: {
        entry: ImportGiftEntry;
        startDate: string;
        endDate: string;
    },
): boolean {
    const current = {
        name: mapped.entry.legacyGiftId,
        contents: mapped.entry.contents,
        wrapperGameElementId: mapped.entry.wrapperGameElementId,
        tutorial: existing?.tutorial ?? false,
        defaultClaimed: existing?.defaultClaimed ?? false,
        minimumVersion: existing?.minimumVersion ?? null,
    };
    const previous = _.pick(existing, Object.keys(current));

    return !_.isEqual(current, previous);
}

export const ImportGiftsButton = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [jsonText, setJsonText] = useState('');
    const [isImporting, setIsImporting] = useState(false);
    const [importError, setImportError] = useState<string | null>(null);

    const giftsApi = useGiftApi();

    const legacyGiftsQuery = giftsApi.useQuery(
        'get',
        '/gift/api/v1/admin/gifts/all',
    );
    const giftsV2Query = giftsApi.useQuery('get', '/gift/api/v2/admin/gifts');
    const { updateGift, publishGift, updatePublication } = useGiftAdmin();

    const parseResult = useMemo(() => {
        if (!jsonText.trim()) return null;
        return parseImportJson(jsonText);
    }, [jsonText]);

    const preview = useMemo(() => {
        if (!parseResult || typeof parseResult === 'string') return null;
        if (!legacyGiftsQuery.data || !giftsV2Query.data) return null;
        return computePreview(
            parseResult,
            legacyGiftsQuery.data,
            giftsV2Query.data,
        );
    }, [parseResult, legacyGiftsQuery.data, giftsV2Query.data]);

    const handleImport = async () => {
        if (!preview) return;

        setIsImporting(true);
        setImportError(null);
        const existingGiftsById = new Map(
            (giftsV2Query.data?.gifts ?? []).map((g) => [g.id, g]),
        );
        try {
            const allGifts = [...preview.newGifts, ...preview.updateGifts];
            for (const { entry } of allGifts) {
                const existing = existingGiftsById.get(entry.id);
                const putGiftRequestDTO: GiftSchemas['PutGiftRequestDTO'] = {
                    name: entry.legacyGiftId,
                    contents: entry.contents,
                    wrapperGameElementId: entry.wrapperGameElementId,
                    tutorial: existing?.tutorial ?? false,
                    defaultClaimed: existing?.defaultClaimed ?? false,
                    minimumVersion: existing?.minimumVersion || null,
                    jiraIssueKey: existing?.jiraIssueKey || null,
                };
                await updateGift(entry.id, putGiftRequestDTO);
            }

            for (const {
                entry,
                startDate,
                endDate,
                action,
            } of preview.publicationActions) {
                if (action.type === 'add') {
                    await publishGift(entry.id, { startDate, endDate });
                } else if (action.type === 'update-window') {
                    await updatePublication(entry.id, action.publicationId, {
                        startDate: action.startDate,
                        endDate: action.endDate,
                    });
                }
            }

            setIsOpen(false);
            setJsonText('');
        } catch (error) {
            setImportError(`Import failed: ${getApiErrorMessage(error)}`);
        } finally {
            setIsImporting(false);
            giftsV2Query.refetch();
        }
    };

    const isDataLoading = legacyGiftsQuery.isLoading || giftsV2Query.isLoading;
    const parseError = typeof parseResult === 'string' ? parseResult : null;
    const changeCount =
        preview === null
            ? 0
            : preview.newGifts.length +
              preview.updateGifts.length +
              preview.publicationActions.length;
    const canImport = preview !== null && changeCount > 0;

    return (
        <>
            <Button
                startIcon={<Icon name="upload" />}
                onClick={() => setIsOpen(true)}
                variant="contained"
            >
                Import
            </Button>
            <Dialog
                open={isOpen}
                onClose={() => !isImporting && setIsOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Import Gifts from Legacy</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            label="Paste JSON"
                            multiline
                            minRows={6}
                            maxRows={16}
                            value={jsonText}
                            onChange={(e) => setJsonText(e.target.value)}
                            placeholder={
                                '[\n  { "legacyGiftId": "...", "id": "...", "contents": [...], "wrapperGameElementId": "..." }\n]'
                            }
                            fullWidth
                        />

                        {isDataLoading && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                }}
                            >
                                <CircularProgress size={16} />
                                <Typography variant="body2">
                                    Loading gift data...
                                </Typography>
                            </Box>
                        )}

                        {parseError && (
                            <Alert severity="error">{parseError}</Alert>
                        )}

                        {preview && (
                            <>
                                {preview.errors.length > 0 && (
                                    <Alert severity="info">
                                        Skipping {preview.errors.length} gift(s)
                                        not found in legacy system:
                                        {preview.errors.map((err, i) => (
                                            <div key={i}>{err}</div>
                                        ))}
                                        <br />
                                        Did you expect to import these? Ensure
                                        they have been synced in the{' '}
                                        <Link to={RouteTo.Gifts()}>
                                            legacy gift
                                        </Link>{' '}
                                        system with the Google Sheets
                                    </Alert>
                                )}
                                <Typography variant="body1">
                                    {changeCount === 0 ? (
                                        'No changes to import'
                                    ) : (
                                        <>
                                            <strong>
                                                Import {preview.newGifts.length}{' '}
                                                new
                                            </strong>
                                            {', '}
                                            <strong>
                                                Update{' '}
                                                {preview.updateGifts.length}{' '}
                                                gifts
                                            </strong>
                                            {preview.publicationActions.length >
                                                0 && (
                                                <>
                                                    {', '}
                                                    <strong>
                                                        {
                                                            preview
                                                                .publicationActions
                                                                .length
                                                        }{' '}
                                                        publication change(s)
                                                    </strong>
                                                </>
                                            )}
                                        </>
                                    )}
                                </Typography>
                            </>
                        )}

                        {importError && (
                            <Alert severity="error">{importError}</Alert>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setIsOpen(false);
                            setJsonText('');
                        }}
                        disabled={isImporting}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleImport}
                        disabled={!canImport || isImporting}
                        startIcon={
                            isImporting ? (
                                <CircularProgress size={16} />
                            ) : undefined
                        }
                    >
                        {isImporting ? 'Importing...' : 'Import'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};
